package com.gsb.vyapar.bhagya.presentation.common.menu

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.Checkbox
import androidx.compose.material.CheckboxDefaults
import androidx.compose.material.DropdownMenuItem
import androidx.compose.material.ExperimentalMaterialApi
import androidx.compose.material.ExposedDropdownMenuBox
import androidx.compose.material.ExposedDropdownMenuDefaults
import androidx.compose.material.OutlinedTextField
import androidx.compose.material.TextFieldColors
import androidx.compose.material.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment.Companion.CenterVertically
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color.Companion.Black
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gsb.vyapar.bhagya.presentation.common.edittext.getEditTextColor
import com.gsb.vyapar.bhagya.theme.*
import com.gsb.vyapar.bhagya.presentation.common.text.Text14Regular
import com.gsb.vyapar.bhagya.presentation.common.text.Text14RegularB
import com.gsb.vyapar.bhagya.presentation.common.text.Text14RegularS

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun OutlinedExpandedMenu(
    label: String = "",
    selectedIndex: Int,
    options: List<String>,
    onSelected: (Int) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    ExposedDropdownMenuBox(
        modifier = Modifier.fillMaxWidth(),
        expanded = expanded,
        onExpandedChange = { expanded = !expanded }) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            readOnly = true,
            value = if (selectedIndex >= 0 && selectedIndex < options.size) options[selectedIndex] else "",
            onValueChange = { },
            label = { Text14Regular(text = label, color = message) },
            textStyle = TextStyle(fontFamily = interMedium(), fontSize = 14.sp, fontWeight = FontWeight(500)),
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            colors = getMenuOutlineColor(),
        )
        ExposedDropdownMenu(modifier = Modifier
            .fillMaxWidth()
            .background(white),
            expanded = expanded,
            onDismissRequest = { expanded = false }) {
            options.forEachIndexed { index: Int, selectionOption: String ->
                DropdownMenuItem(modifier = Modifier.fillMaxWidth(),
                    onClick = {
                        expanded = false
                        onSelected(index)
                    }
                ) { Text14Regular(text = selectionOption, color = Black) }
            }
        }
    }
}

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun OutlinedExpandedMenu(
    label: String = "",
    options: List<String>,
    selectedIndex: Int,
    onSelected: (String, Int) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }

    ExposedDropdownMenuBox(
        modifier = Modifier.fillMaxWidth(),
        expanded = expanded,
        onExpandedChange = { expanded = !expanded }) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            readOnly = true,
            value = if (selectedIndex >= 0 && selectedIndex < options.size) options[selectedIndex] else "",
            onValueChange = { },
            label = { Text14Regular(text = label, color = message) },
            textStyle = TextStyle(fontFamily = interMedium(), fontSize = 14.sp, fontWeight = FontWeight(500)),
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            colors = getMenuOutlineColor(),
        )
        ExposedDropdownMenu(modifier = Modifier
            .fillMaxWidth()
            .background(white),
            expanded = expanded,
            onDismissRequest = { expanded = false }) {
            options.forEachIndexed { index: Int, selectionOption: String ->
                DropdownMenuItem(modifier = Modifier.fillMaxWidth(),
                    onClick = {
                        expanded = false
                        onSelected(selectionOption, index)
                    }
                ) { Text14Regular(text = selectionOption, color = Black) }
            }
        }
    }
}

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun OutlinedExpandedSelectMenu(
    label: String,
    options: List<String>,
    onSelectionChanged: (List<String>) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    var selectedIndices by remember { mutableStateOf(listOf<Int>()) }

    fun selectAll() {
        selectedIndices = options.indices.toList()
        onSelectionChanged(selectedIndices.map { options[it] })
    }

    fun clear() {
        selectedIndices = listOf()
        onSelectionChanged(selectedIndices.map { options[it] })
    }

    LaunchedEffect(options) {
        clear()
    }

    ExposedDropdownMenuBox(
        modifier = Modifier.fillMaxWidth(),
        expanded = expanded,
        onExpandedChange = { expanded = !expanded }
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            readOnly = true,
            value = options.filterIndexed { index, _ -> selectedIndices.contains(index) }.joinToString(", "),
            onValueChange = {},
            label = { Text14Regular(text = label, color = message) },
            textStyle = TextStyle(fontFamily = interMedium(), fontSize = 14.sp, fontWeight = FontWeight(500)),
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            colors = getEditTextColor()
        )

        ExposedDropdownMenu(
            modifier = Modifier.fillMaxWidth(),
            expanded = expanded,
            onDismissRequest = { expanded = false }
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(8.dp),
                verticalAlignment = CenterVertically
            ) {
                Row(
                    modifier = Modifier.clickable { selectAll() },
                    verticalAlignment = CenterVertically
                ) {
                    Spacer(modifier = Modifier.width(8.dp))
                    Checkbox(
                        checked = selectedIndices.size == options.size && options.isNotEmpty(),
                        onCheckedChange = { if (selectedIndices.size == options.size) clear() else selectAll() },
                        colors = CheckboxDefaults.colors(checkedColor = green)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text14RegularB(text = "Select All", color = black)
                }
                Spacer(modifier = Modifier.weight(1f))
                Text14Regular(
                    text = "Clear",
                    color = black,
                    modifier = Modifier.clickable { clear() }
                )
            }
            options.forEachIndexed { index, option ->
                DropdownMenuItem(
                    modifier = Modifier.fillMaxWidth(),
                    onClick = {
                        val newSelection = if (selectedIndices.contains(index)) selectedIndices - index else selectedIndices + index
                        selectedIndices = newSelection
                        onSelectionChanged(selectedIndices.map { options[it] })
                    }
                ) {
                    Row(verticalAlignment = CenterVertically) {
                        Checkbox(
                            checked = selectedIndices.contains(index),
                            onCheckedChange = {
                                val newSelection = if (selectedIndices.contains(index)) selectedIndices - index else selectedIndices + index
                                selectedIndices = newSelection
                                onSelectionChanged(selectedIndices.map { options[it] })
                            },
                            colors = CheckboxDefaults.colors(checkedColor = green)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text14RegularS(text = option, color = black, modifier = Modifier.fillMaxWidth())
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun OutlinedExpandedSelectMenu(
    label: String,
    options: List<String>,
    selectedIndices: List<Int>,
    onSelectionChanged: (List<Int>) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }

    fun selectAll() {
        onSelectionChanged(options.indices.toList())
    }

    fun clear() {
        onSelectionChanged(listOf())
    }

    ExposedDropdownMenuBox(
        modifier = Modifier.fillMaxWidth(),
        expanded = expanded,
        onExpandedChange = { expanded = !expanded }
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            readOnly = true,
            value = options.filterIndexed { index, _ -> selectedIndices.contains(index) }.joinToString(", "),
            onValueChange = {},
            label = { Text14Regular(text = label, color = message) },
            textStyle = TextStyle(fontFamily = interMedium(), fontSize = 14.sp, fontWeight = FontWeight(500)),
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            colors = getEditTextColor()
        )

        ExposedDropdownMenu(
            modifier = Modifier.fillMaxWidth(),
            expanded = expanded,
            onDismissRequest = { expanded = false }
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(8.dp),
                verticalAlignment = CenterVertically
            ) {
                Row(
                    modifier = Modifier.clickable { selectAll() },
                    verticalAlignment = CenterVertically
                ) {
                    Spacer(modifier = Modifier.width(8.dp))
                    Checkbox(
                        checked = selectedIndices.size == options.size && options.isNotEmpty(),
                        onCheckedChange = { if (selectedIndices.size == options.size) clear() else selectAll() },
                        colors = CheckboxDefaults.colors(checkedColor = green)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text14RegularB(text = "Select All", color = black)
                }
                Spacer(modifier = Modifier.weight(1f))
                Text14Regular(
                    text = "Clear",
                    color = black,
                    modifier = Modifier.clickable { clear() }
                )
            }
            options.forEachIndexed { index, option ->
                DropdownMenuItem(
                    modifier = Modifier.fillMaxWidth(),
                    onClick = {
                        val newSelection = if (selectedIndices.contains(index)) selectedIndices - index else selectedIndices + index
                        onSelectionChanged(newSelection)
                    }
                ) {
                    Row(verticalAlignment = CenterVertically) {
                        Checkbox(
                            checked = selectedIndices.contains(index),
                            onCheckedChange = {
                                val newSelection = if (selectedIndices.contains(index)) selectedIndices - index else selectedIndices + index
                                onSelectionChanged(newSelection)
                            },
                            colors = CheckboxDefaults.colors(checkedColor = green)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text14Regular(text = option, color = black)
                    }
                }
            }
        }
    }
}

@Composable
fun getMenuOutlineColor(): TextFieldColors {
    return TextFieldDefaults.outlinedTextFieldColors(
        backgroundColor = white,
        textColor = Black,
        cursorColor = Black,
        focusedBorderColor = focussed,
        unfocusedBorderColor = focussed,
        unfocusedLabelColor = unfocussed,
        focusedLabelColor = message
    )
}